import { useCallback, useEffect, useState, type SyntheticEvent } from "react";
import type { JobLookupRow } from "./JobLookupSearch.vm";

interface JobLookupHighlightArgs {
	options: JobLookupRow[];
	open: boolean;
}

/**
 * Which row of the dropdown is highlighted.
 *
 * `onHighlightChange` reports the keyboard, the mouse and touch, and never `autoHighlight`'s own move to the top row, so
 * a row from those events counts only while it is on screen and nothing has reset MUI's highlight: a new response (the
 * list remounts per response, and its rows are new objects), a change of text, or the dropdown closing. Anywhere else
 * the highlight is on the top row.
 */
export const useJobLookupHighlight = ({ options, open }: JobLookupHighlightArgs) => {
	const [highlighted, setHighlighted] = useState<JobLookupRow | null>(null);

	const reset = useCallback(() => setHighlighted(null), []);

	useEffect(() => {
		if (!open) reset();
	}, [open, reset]);

	const onHighlightChange = (_event: SyntheticEvent, row: JobLookupRow | null) => setHighlighted(row);

	const row = highlighted && options.includes(highlighted) ? highlighted : (options[0] ?? null);

	return { row, reset, onHighlightChange };
};
